import React, { memo, useMemo, useState } from "react";
import { Modal, Tabs, Avatar, Empty, Spin, Button, message as antdMessage } from "antd";
import { FileDown } from "lucide-react";
import { useGetMemberConfirmation } from "../../hooks/queries/chat";
import { chatApi } from "../../api/chat";
import styles from "./ConfirmationDetailsModal.module.scss";

interface ConfirmationDetailsModalProps {
  messageId: string;
  roomId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ConfirmationDetailsModal: React.FC<ConfirmationDetailsModalProps> = ({
  messageId,
  roomId,
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<string>("confirmed");
  const [isExporting, setIsExporting] = useState(false);

  // Fetch all confirmations
  const { data: allData, isLoading: isLoadingAll } = useGetMemberConfirmation({
    messageId,
    roomId,
  });

  // Fetch confirmed members
  const { data: confirmedData, isLoading: isLoadingConfirmed } = useGetMemberConfirmation({
    messageId,
    roomId,
    ack: true,
  });

  // Fetch not confirmed members
  const { data: notConfirmedData, isLoading: isLoadingNotConfirmed } = useGetMemberConfirmation({
    messageId,
    roomId,
    ack: false,
  });

  const allMembers = allData?.Data || [];
  const confirmedMembers = confirmedData?.Data || [];
  const notConfirmedMembers = notConfirmedData?.Data || [];

  // Get current list based on active tab
  const currentList = useMemo(() => {
    if (activeTab === "confirmed") return confirmedMembers;
    return notConfirmedMembers;
  }, [activeTab, confirmedMembers, notConfirmedMembers]);

  const isLoading = useMemo(() => {
    if (activeTab === "confirmed") return isLoadingConfirmed;
    return isLoadingNotConfirmed;
  }, [activeTab, isLoadingConfirmed, isLoadingNotConfirmed]);

  // Handle export report
  const handleExportReport = async () => {
    try {
      setIsExporting(true);
      antdMessage.loading({ content: "Đang xuất báo cáo...", key: "export-report" });
      const blob = await chatApi.exportMemberConfirmation(messageId || "", roomId || "");

      // Create download link
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Bao-cao-xac-nhan-${messageId?.substring(0, 8)}.xlsx`);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      antdMessage.success({ content: "Đã xuất báo cáo thành công", key: "export-report" });
    } catch (error) {
      console.error("Lỗi khi xuất báo cáo:", error);
      antdMessage.error({ content: "Có lỗi xảy ra khi xuất báo cáo", key: "export-report" });
    } finally {
      setIsExporting(false);
    }
  };

  // Build tabs
  const tabItems = [
    {
      key: "confirmed",
      label: (
        <div className={styles.tabLabel}>
          <span>Đã xác nhận</span>
          <span className={styles.count}>{confirmedMembers.length}</span>
        </div>
      ),
    },
    {
      key: "notConfirmed",
      label: (
        <div className={styles.tabLabel}>
          <span>Chưa xác nhận</span>
          <span className={styles.count}>{notConfirmedMembers.length}</span>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="Danh sách xác nhận"
      open={isOpen}
      onCancel={onClose}
      footer={
        <div className={styles.modalFooter}>
          <Button
            type="primary"
            icon={<FileDown size={16} />}
            onClick={handleExportReport}
            loading={isExporting}
            className={styles.exportButton}
          >
            Xuất báo cáo
          </Button>
        </div>
      }
      width={460}
      centered
      className={styles.confirmationModal}
      bodyStyle={{
        padding: 0,
        maxHeight: "500px",
      }}
    >
      <div className={styles.modalContent}>
        {/* Tabs for filtering */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className={styles.confirmationTabs}
          size="small"
        />

        {/* Member list */}
        <div className={styles.confirmationList}>
          {isLoading ? (
            <div className={styles.loading}>
              <Spin />
              <p>Đang tải...</p>
            </div>
          ) : currentList.length === 0 ? (
            <div className={styles.empty}>
              <Empty
                description={activeTab === "confirmed" ? "Chưa có ai xác nhận" : "Tất cả đã xác nhận"}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          ) : (
            currentList.map((member) => (
              <div key={member.UserId} className={styles.confirmationItem}>
                <Avatar src={member.AvatarUrl} size={40} className={styles.avatar}>
                  {!member.AvatarUrl && member.ContactName?.charAt(0).toUpperCase()}
                </Avatar>
                <div className={styles.info}>
                  <div className={styles.name}>{member.ContactName}</div>
                  {member.AckTime && (
                    <div className={styles.time}>
                      {new Date(member.AckTime).toLocaleString("vi-VN", {
                        day: "2-digit",
                        month: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  )}
                  {member.Description && <div className={styles.description}>{member.Description}</div>}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
};

export default memo(ConfirmationDetailsModal);
